/**
 * Print-format dispatcher. Maps a Storefront-internal format slug to the
 * matching builder (sheet or thermal) and exposes a single `buildPDFForFormat`
 * helper so consumers (e.g. `useLabelPrint`) don't have to switch on kind.
 *
 * Slug names are purely internal — there's no server-side registry anymore
 * (the Python service was rejected in the 2026-05-18 reversal of Spike 1.3).
 * Adding a new format = adding a row to PRINT_FORMATS.
 */

import { z } from 'zod';
import type jsPDF from 'jspdf';
import { buildLabelSheetPDF, type LabelItem } from './build-sheet';
import { buildThermalLabelPDF } from './build-thermal';
import {
  AVERY_5160,
  AVERY_5163,
  AVERY_5293,
  THERMAL_BROTHER_QL_62MM,
  THERMAL_BROTHER_QL_62MM_DM,
  THERMAL_DYMO_30334,
  THERMAL_DYMO_30334_DM,
  THERMAL_ROUND_1IN,
  THERMAL_2X1,
  THERMAL_2X1_DM,
  type AverySheetSpec,
  type ThermalLabelSpec,
} from './specs';

export const PrintFormatSlugSchema = z.enum([
  'avery_5160',
  'avery_5163',
  'avery_5293',
  'brother_ql_62mm',
  'dymo_30334',
  'round_1in',
  'brother_ql_62mm_dm',
  'dymo_30334_dm',
  // vendor#1103 — the 2 × 1 stock in actual use. Added last so the enum's
  // existing order (and anything persisted against it) is untouched.
  'thermal_2x1',
  'thermal_2x1_dm',
]);
export type PrintFormatSlug = z.infer<typeof PrintFormatSlugSchema>;

export type PrintFormatEntry =
  | { kind: 'sheet'; spec: AverySheetSpec }
  | { kind: 'thermal'; spec: ThermalLabelSpec };

export const PRINT_FORMATS: Record<PrintFormatSlug, PrintFormatEntry> = {
  avery_5160: { kind: 'sheet', spec: AVERY_5160 },
  avery_5163: { kind: 'sheet', spec: AVERY_5163 },
  avery_5293: { kind: 'sheet', spec: AVERY_5293 },
  brother_ql_62mm: { kind: 'thermal', spec: THERMAL_BROTHER_QL_62MM },
  dymo_30334: { kind: 'thermal', spec: THERMAL_DYMO_30334 },
  round_1in: { kind: 'thermal', spec: THERMAL_ROUND_1IN },
  brother_ql_62mm_dm: { kind: 'thermal', spec: THERMAL_BROTHER_QL_62MM_DM },
  dymo_30334_dm: { kind: 'thermal', spec: THERMAL_DYMO_30334_DM },
  thermal_2x1: { kind: 'thermal', spec: THERMAL_2X1 },
  thermal_2x1_dm: { kind: 'thermal', spec: THERMAL_2X1_DM },
};

export function buildPDFForFormat(format: PrintFormatSlug, items: LabelItem[]): jsPDF {
  const entry = PRINT_FORMATS[format];
  if (entry.kind === 'sheet') return buildLabelSheetPDF(items, entry.spec);
  return buildThermalLabelPDF(items, entry.spec);
}

/** Real per-label geometry and labels-per-sheet for a format (vendor#411 /
 *  vendor#350), read from the format's own spec rather than re-derived. A
 *  thermal format is one label per PDF page (`build-thermal.ts`'s own
 *  comment: "one label per 'page' of a small-page-size PDF"), so it counts
 *  as 1-per-sheet. */
export interface LabelFormatGeometry {
  widthIn: number;
  heightIn: number;
  /** Labels per physical sheet — 1 for every thermal format. */
  perSheet: number;
}

export function formatGeometry(format: PrintFormatSlug): LabelFormatGeometry {
  const entry = PRINT_FORMATS[format];
  if (entry.kind === 'sheet') {
    return {
      widthIn: entry.spec.labelWidthIn,
      heightIn: entry.spec.labelHeightIn,
      perSheet: entry.spec.rows * entry.spec.cols,
    };
  }
  return { widthIn: entry.spec.widthIn, heightIn: entry.spec.heightIn, perSheet: 1 };
}
